import type { AuthUserDto, ExportDto, PortalOverviewDto } from '../generated/contracts';
import { signOut } from '../core/AccountGate';
import { PortalShell } from './portal/shell';
import {
  deleteExport,
  exportDownloadUrl,
  getExports,
  getExportText,
  getPortalOverview,
  NotSignedInError,
  renameExport,
  transcribeExport,
} from '../net/api';
import { rasterizeExportToPng } from './ExportRasterizer';

/**
 * `ExportKind` as it actually arrives on the wire — a bare number, not a TypeScript enum
 * (CLAUDE.md: "`ExportKind` serialises as a number on the wire — no `JsonStringEnumConverter`
 * configured"). Mirrors `Tunet.Contracts.Dtos.ExportKind` by hand for that reason, the same
 * "the codegen cannot carry this" shape a handful of other C# enums already need.
 */
const EXPORT_KIND_MERMAID = 1;

/**
 * The portal's exports page (`plan/identity-and-portal.md`, "Exports, as the portal's
 * first real page"): the room's accumulated whiteboard exports, newest first.
 *
 * Viewing is the primary action, per the owner's own verdict on the first shipped cut:
 * "Keeping all wiped boards forever with just a date and no way to preview them isn't that
 * useful. Default action should really be to just view them." Every row shows an inline
 * thumbnail — an `<img>` pointed straight at the authenticated download route, which works
 * unassisted because the request is same-origin (the session cookie rides along exactly
 * like it does for `fetch`, with no `crossorigin` attribute needed — confirmed in a real
 * browser against a signed-in session, not assumed) — and clicking it, or the "View"
 * button, opens the same SVG larger in an overlay. Downloading is still offered, just no
 * longer the only thing a row does.
 *
 * Rename and delete are both REST mutations (`net/api.ts`'s own remarks on why), not hub
 * methods — an export has no lock and no live room group to broadcast into. Delete asks
 * first: an export is often the only remaining record of a board that has since been
 * wiped, and v1 has no undo, so the button turns into an inline "Delete it? / Cancel"
 * rather than firing on the first click.
 *
 * Fetches the room id from `/api/bootstrap` rather than taking one as a constructor
 * argument: v1 has exactly one room, and this keeps the page reachable from a bare
 * `/portal/exports` route (`Router.ts`'s own "every route is a bare path segment" rule)
 * without a room id ever appearing in the URL.
 *
 * Board OCR — reading the handwriting on the whiteboard export — lives here too, as an
 * explicit "Read text" button per row rather than anything automatic: a real Gemini call
 * runs per read, and reading is not free. `ExportRasterizer.ts` turns the same SVG the
 * thumbnail already shows into a PNG in the browser; the result is stored and shown
 * *alongside* the export (`buildTextCell`), never written back over the strokes themselves.
 * See `buildTextCell`'s own remarks for the three states a row can be in and why they stay
 * visually distinct.
 */
export interface PortalExportsDeps {
  getPortalOverview: () => Promise<PortalOverviewDto>;
  getExports: (diagramId: string) => Promise<ExportDto[]>;
  renameExport: (diagramId: string, exportId: string, name: string) => Promise<ExportDto>;
  deleteExport: (diagramId: string, exportId: string) => Promise<void>;

  /** Draws the export's own SVG onto a `<canvas>` and reads a PNG back out — the browser
   *  half of board OCR (`ExportRasterizer.ts`'s own remarks on why this runs client-side).
   *  Not exercised by any test in this file: jsdom has no real canvas rasteriser, the same
   *  reason `NullEngine` never rasterises in the Babylon half of this project — a test that
   *  pretended otherwise would prove nothing. Callers only ever get to prove this dependency
   *  was *called*, with what argument, not what it draws. */
  rasterizeExport: (downloadUrl: string) => Promise<Blob>;

  transcribeExport: (diagramId: string, exportId: string, image: Blob) => Promise<ExportDto>;

  /** The raw text of a text-shaped export (Mermaid source) — the portal's own preview and
   *  "selectable text" requirement for it, where an SVG export instead points an `<img>`
   *  straight at the download route and needs no fetch at all. */
  getExportText: (diagramId: string, exportId: string) => Promise<string>;
}

const defaultDeps: PortalExportsDeps = {
  getPortalOverview,
  getExports,
  renameExport,
  deleteExport,
  rasterizeExport: rasterizeExportToPng,
  transcribeExport,
  getExportText,
};

export class PortalExports {
  private readonly root: HTMLElement;
  private readonly deps: PortalExportsDeps;

  private diagramId: string | null = null;

  private diagramName: string | null = null;
  private exports: ExportDto[] = [];

  /** The one export id whose row is mid-confirmation. Only one at a time — opening a
   *  second row's confirm silently drops the first, which is fine: nothing has happened
   *  yet, there is nothing to lose. */
  private confirmingDeleteId: string | null = null;

  /** The one export id whose row is mid-rename. Same one-at-a-time shape as delete. */
  private renamingId: string | null = null;

  /** The one export id currently being read by Gemini — reading takes a real network round
   *  trip (rasterise, upload, wait on a model call), so this both disables a second click on
   *  the same row and lets the button say so instead of looking inert. More than one row can
   *  legitimately be reading at once (nothing here serialises across rows), so this is a set,
   *  not a single id the way confirmingDeleteId/renamingId are — those are deliberately
   *  one-at-a-time UI state, this is genuinely concurrent network state. */
  private readonly transcribingIds = new Set<string>();

  /** A per-row read failure message — reading is not free (real Gemini calls cost money), so
   *  a failure must say so plainly rather than silently reverting to whatever the row already
   *  showed. Cleared on the next read attempt for that row. */
  private readonly transcribeErrors = new Map<string, string>();

  /**
   * Fetched Mermaid source, keyed by export id — a text-shaped export (unlike an SVG one)
   * has nothing an `<img src>` can render, so its own preview and viewer both need an
   * explicit fetch. Cached here so the preview cell and a later "View" click share one
   * fetch rather than each firing its own.
   */
  private readonly mermaidText = new Map<string, string>();

  /** Export ids whose Mermaid text is currently being fetched — same "disable a second
   *  click, let the row say so" reasoning as {@link transcribingIds}. */
  private readonly loadingMermaidIds = new Set<string>();

  /** A per-row Mermaid fetch failure — cleared on the next attempt for that row, same shape
   *  as {@link transcribeErrors}. */
  private readonly mermaidTextErrors = new Map<string, string>();

  private viewerOverlay: HTMLElement | null = null;

  private readonly shell: PortalShell;

  constructor(
    host: HTMLElement,
    account: AuthUserDto,
    private readonly onBack: () => void,
    deps: Partial<PortalExportsDeps> = {},
  ) {
    this.deps = { ...defaultDeps, ...deps };

    // The same shell every portal page wears — a persistent rail carrying the surface's three
    // destinations, the account, and the way out. The page this replaces built its own top bar
    // per route, which is how "Controls" and "Exports" came to be rendered as sign-out buttons:
    // `.portal-logout` was the only button class that existed.
    this.shell = new PortalShell(host, account, '/portal/exports', {
      go: (route) => {
        if (route === '/portal') {
          this.onBack();
        }
      },
    });

    this.root = this.shell.main;
    this.root.innerHTML = `
      <header class="page-head">
        <h1>Exports</h1>
        <p>Whiteboards and diagrams saved out of your rooms.</p>
      </header>

      <section class="card section card-padded" data-exports-section aria-labelledby="exports-heading">
        <h2 id="exports-heading">Exports</h2>
        <div data-exports-body>Loading…</div>
      </section>
    `;

    void this.loadExports();
  }

  dispose(): void {
    this.viewerOverlay?.remove();
    this.viewerOverlay = null;
    this.shell.dispose();
  }

  private async loadExports(): Promise<void> {
    const body = this.query('[data-exports-body]');

    try {
      // WHICH DIAGRAM'S EXPORTS, ANSWERED EXPLICITLY (plan/room-to-diagram.md, task R3).
      //
      // This used to call GET /api/bootstrap and list the resolved *room*'s exports. That was
      // defensible while every org had one permanent room; it became wrong the moment rooms
      // started coming and going (SPEC-153), because bootstrap answers "the first room of your
      // team by name" — which is now whichever ephemeral room happens to exist at that instant.
      // One person opening this page would be shown the exports of the diagram somebody else in
      // their org was working in, with nothing on screen saying so. And with no live room at all
      // it answered 503, making every export in the org unreachable, including the automatic
      // snapshot SPEC-100 takes before a whiteboard is cleared — the one mechanism that exists so
      // deliberately destroyed ink is recoverable.
      //
      // An explicit ?diagramId= wins; otherwise the most recently changed diagram, which the
      // overview already returns in that order. Either way the name is rendered in the heading,
      // so which diagram this is showing is never something the reader has to infer.
      const requested = new URLSearchParams(window.location.search).get('diagramId');

      if (requested !== null) {
        this.diagramId = requested;
        this.diagramName = null;
      } else {
        const overview = await this.deps.getPortalOverview();
        const newest = overview.diagrams[0] ?? null;

        if (newest === null) {
          this.diagramId = null;
          this.renderNoDiagrams();
          return;
        }

        this.diagramId = newest.id;
        this.diagramName = newest.name;
      }

      this.exports = await this.deps.getExports(this.diagramId);
      this.render();
    } catch (error) {
      // Same "NotSignedInError routes to sign-in, anything else surfaces the server's own
      // detail" split as `PortalHome.handleLoadFailure` — see that method's own doc for the
      // full reasoning (`net/api.ts`'s `NotSignedInError` doc names this exact caller-side
      // gap: as of that change, no caller anywhere checked `instanceof NotSignedInError`).
      // Not pulled into a shared helper across the two files: this is the only fetch in this
      // class with its own bare catch, so a one-off inline branch costs less than a shared
      // abstraction with one real caller on each side.
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      body.innerHTML = '';
      body.textContent = error instanceof Error ? error.message : "Couldn't load exports. Try reloading the page.";
    }
  }

  /**
   * Names the diagram these exports belong to, so the page never silently shows one diagram's
   * output under a heading that could mean any of them. Skipped when the caller asked for a
   * specific `?diagramId=` — they already know which one, and the id is in their own URL.
   */
  private renderWhichDiagram(): void {
    const existing = this.root.querySelector('[data-exports-which]');
    existing?.remove();

    if (this.diagramName === null) {
      return;
    }

    const line = document.createElement('p');
    line.className = 'dashboard-note';
    line.setAttribute('data-exports-which', '');
    line.textContent = `Showing exports from ${this.diagramName}, your most recently changed diagram.`;

    this.query('[data-exports-body]').before(line);
  }

  /** No diagrams at all — a new org, before anybody has made one. */
  private renderNoDiagrams(): void {
    const body = this.query('[data-exports-body]');
    body.innerHTML = '';
    body.textContent = 'No diagrams yet, so there is nothing to export. Make one from the portal home.';
  }

  private render(): void {
    const body = this.query('[data-exports-body]');
    body.innerHTML = '';

    this.renderWhichDiagram();

    if (this.exports.length === 0) {
      body.textContent = 'No exports yet — clearing the whiteboard, or an explicit export, creates one.';
      return;
    }

    const diagramId = this.diagramId;
    if (diagramId === null) {
      return;
    }

    const table = document.createElement('table');
    table.className = 'dashboard-member-table';
    table.innerHTML =
      '<thead><tr><th>Preview</th><th>Name</th><th>Created</th><th>By</th><th>Size</th><th>Text</th><th></th></tr></thead>';

    const tbody = document.createElement('tbody');

    for (const item of this.exports) {
      tbody.appendChild(this.buildRow(diagramId, item));
    }

    table.appendChild(tbody);
    body.appendChild(table);
  }

  private buildRow(diagramId: string, item: ExportDto): HTMLTableRowElement {
    const row = document.createElement('tr');
    const displayName = describeName(item);

    const previewCell = document.createElement('td');
    previewCell.appendChild(this.buildPreviewCell(diagramId, item, displayName));

    const nameCell = document.createElement('td');
    nameCell.appendChild(this.buildNameCell(diagramId, item, displayName));

    const createdCell = document.createElement('td');
    createdCell.textContent = formatCreatedAt(item.createdAt);

    const byCell = document.createElement('td');
    byCell.textContent = item.createdByDisplayName ?? 'Gemma';

    const sizeCell = document.createElement('td');
    sizeCell.textContent = formatSize(item.sizeBytes);

    const textCell = document.createElement('td');
    textCell.appendChild(this.buildTextCell(diagramId, item));

    const actionsCell = document.createElement('td');
    actionsCell.appendChild(this.buildActionsCell(diagramId, item, displayName));

    row.append(previewCell, nameCell, createdCell, byCell, sizeCell, textCell, actionsCell);
    return row;
  }

  /**
   * The preview cell — an `<img>` pointed at the download route for an SVG (whiteboard)
   * export, exactly as before; a selectable `<pre>` of the fetched Mermaid source for a
   * text-shaped one, since nothing an `<img>` renders exists for plain text. Mermaid text
   * is fetched lazily, once per export id ({@link mermaidText}'s own cache), and shown as
   * source — this project adds no client-side Mermaid renderer (a deliberate constraint,
   * not an oversight), so "selectable text so it can be copied" is the whole feature.
   */
  private buildPreviewCell(diagramId: string, item: ExportDto, displayName: string): HTMLElement {
    if (item.kind !== EXPORT_KIND_MERMAID) {
      const thumb = document.createElement('img');
      thumb.className = 'export-thumb';
      thumb.src = exportDownloadUrl(diagramId, item.id);
      thumb.alt = `Preview of ${displayName}`;
      thumb.addEventListener('click', () => this.openViewer(diagramId, item));
      return thumb;
    }

    const cached = this.mermaidText.get(item.id);

    if (cached !== undefined) {
      const pre = document.createElement('pre');
      pre.className = 'export-mermaid-preview';
      pre.textContent = cached;
      pre.addEventListener('click', () => this.openViewer(diagramId, item));
      return pre;
    }

    const error = this.mermaidTextErrors.get(item.id);
    if (error) {
      const errorLine = document.createElement('div');
      errorLine.className = 'export-danger export-text-status';
      errorLine.textContent = error;
      return errorLine;
    }

    if (!this.loadingMermaidIds.has(item.id)) {
      void this.ensureMermaidText(diagramId, item.id);
    }

    const loading = document.createElement('div');
    loading.className = 'export-text-status';
    loading.textContent = 'Loading…';
    return loading;
  }

  /** Fetches and caches an export's Mermaid text once, re-rendering the list when it
   *  settles — the same "async cell that redraws itself" shape {@link readText} already
   *  uses for board OCR. A no-op if a fetch for this id is already in flight or the text is
   *  already cached, so re-rendering the list (e.g. after an unrelated action) never
   *  re-fires it. */
  private async ensureMermaidText(diagramId: string, exportId: string): Promise<void> {
    if (this.mermaidText.has(exportId) || this.loadingMermaidIds.has(exportId)) {
      return;
    }

    this.loadingMermaidIds.add(exportId);
    this.mermaidTextErrors.delete(exportId);

    try {
      const text = await this.deps.getExportText(diagramId, exportId);
      this.mermaidText.set(exportId, text);
    } catch (error) {
      this.mermaidTextErrors.set(
        exportId,
        error instanceof Error ? error.message : 'Loading the mermaid source failed.',
      );
    } finally {
      this.loadingMermaidIds.delete(exportId);
      this.render();
    }
  }

  /**
   * The board-OCR cell: what Gemini read off this export, or an affordance to ask it to.
   * Three distinct states, deliberately kept visually distinct rather than collapsed to "has
   * text or not" — a client that cannot tell "nobody has asked yet" from "asked, and there
   * was nothing legible" would misrepresent a completed, honest read as an unstarted one.
   *
   *   - never read (`transcribedAt` is null): a "Read text" button.
   *   - read, found nothing (`transcribedAt` set, `transcribedText` null): a plain statement
   *     that nothing legible was found, plus a "Read again" button — handwriting recognition
   *     on marker strokes is imperfect, so a first miss is worth letting someone retry.
   *   - read, found text (both set): the text itself, selectable so it can be copied, with a
   *     small note that this is what the model read, not a claim that it is accurate, plus
   *     "Read again".
   *
   * The text is never editable here and never written back onto the export's own strokes —
   * see this project's own decision that OCR extracts, it does not replace.
   */
  private buildTextCell(diagramId: string, item: ExportDto): HTMLElement {
    const container = document.createElement('div');
    container.className = 'export-text-cell';

    const error = this.transcribeErrors.get(item.id);
    if (error) {
      const errorLine = document.createElement('div');
      errorLine.className = 'export-danger export-text-status';
      errorLine.textContent = error;
      container.appendChild(errorLine);
    }

    if (this.transcribingIds.has(item.id)) {
      const status = document.createElement('div');
      status.className = 'export-text-status';
      status.textContent = 'Reading…';
      container.appendChild(status);
      return container;
    }

    if (item.transcribedAt === null || item.transcribedAt === undefined) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dashboard-room-link';
      button.textContent = 'Read text';
      button.addEventListener('click', () => void this.readText(diagramId, item));
      container.appendChild(button);
      return container;
    }

    if (item.transcribedText === null || item.transcribedText === undefined) {
      const status = document.createElement('div');
      status.className = 'export-text-status export-text-muted';
      status.textContent = 'No legible text found.';
      container.appendChild(status);
    } else {
      const text = document.createElement('div');
      text.className = 'export-text-body';
      text.textContent = item.transcribedText;
      container.appendChild(text);

      const caveat = document.createElement('div');
      caveat.className = 'export-text-status export-text-muted';
      caveat.textContent = 'What the model read — not guaranteed accurate.';
      container.appendChild(caveat);
    }

    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'dashboard-room-link';
    again.textContent = 'Read again';
    again.addEventListener('click', () => void this.readText(diagramId, item));
    container.appendChild(again);

    return container;
  }

  private async readText(diagramId: string, item: ExportDto): Promise<void> {
    this.transcribeErrors.delete(item.id);
    this.transcribingIds.add(item.id);
    this.render();

    try {
      const downloadUrl = exportDownloadUrl(diagramId, item.id);
      const image = await this.deps.rasterizeExport(downloadUrl);
      const updated = await this.deps.transcribeExport(diagramId, item.id, image);
      this.exports = this.exports.map((existing) => (existing.id === item.id ? updated : existing));
    } catch (error) {
      this.transcribeErrors.set(
        item.id,
        error instanceof Error ? error.message : 'Reading the handwriting failed.',
      );
    } finally {
      this.transcribingIds.delete(item.id);
      this.render();
    }
  }

  private buildNameCell(diagramId: string, item: ExportDto, displayName: string): HTMLElement {
    if (this.renamingId !== item.id) {
      const span = document.createElement('span');
      span.textContent = displayName;
      return span;
    }

    const form = document.createElement('form');
    form.className = 'export-name-form';

    const input = document.createElement('input');
    input.className = 'export-name-input';
    input.type = 'text';
    input.value = item.name ?? displayName;
    input.maxLength = 200;

    const save = document.createElement('button');
    save.type = 'submit';
    save.className = 'dashboard-room-link';
    save.textContent = 'Save';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'dashboard-room-link';
    cancel.textContent = 'Cancel';
    cancel.addEventListener('click', () => {
      this.renamingId = null;
      this.render();
    });

    form.append(input, save, cancel);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.commitRename(diagramId, item.id, input.value);
    });

    // So a rename opened from the list is immediately typable, matching how a click-to-edit
    // field is expected to behave.
    queueMicrotask(() => input.focus());

    return form;
  }

  private buildActionsCell(diagramId: string, item: ExportDto, displayName: string): HTMLElement {
    const container = document.createElement('div');
    container.className = 'export-actions';

    if (this.confirmingDeleteId === item.id) {
      const confirmWrap = document.createElement('div');
      confirmWrap.className = 'export-delete-confirm';

      const prompt = document.createElement('span');
      prompt.className = 'export-danger';
      prompt.textContent = 'Delete permanently?';

      const yes = document.createElement('button');
      yes.type = 'button';
      yes.className = 'dashboard-room-link export-danger';
      yes.textContent = 'Delete';
      yes.addEventListener('click', () => void this.commitDelete(diagramId, item.id));

      const no = document.createElement('button');
      no.type = 'button';
      no.className = 'dashboard-room-link';
      no.textContent = 'Cancel';
      no.addEventListener('click', () => {
        this.confirmingDeleteId = null;
        this.render();
      });

      confirmWrap.append(prompt, yes, no);
      container.appendChild(confirmWrap);
      return container;
    }

    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'dashboard-room-link';
    view.textContent = 'View';
    view.addEventListener('click', () => this.openViewer(diagramId, item));

    const download = document.createElement('a');
    download.href = exportDownloadUrl(diagramId, item.id);
    download.download = `${displayName}.${item.kind === EXPORT_KIND_MERMAID ? 'mmd' : 'svg'}`;
    download.textContent = 'Download';
    download.className = 'dashboard-room-link';

    const rename = document.createElement('button');
    rename.type = 'button';
    rename.className = 'dashboard-room-link';
    rename.textContent = 'Rename';
    rename.addEventListener('click', () => {
      this.renamingId = item.id;
      this.render();
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'dashboard-room-link';
    remove.textContent = 'Delete';
    remove.addEventListener('click', () => {
      this.confirmingDeleteId = item.id;
      this.render();
    });

    container.append(view, download, rename, remove);
    return container;
  }

  private async commitRename(diagramId: string, exportId: string, rawName: string): Promise<void> {
    const name = rawName.trim();

    if (name.length === 0) {
      return;
    }

    try {
      const updated = await this.deps.renameExport(diagramId, exportId, name);
      this.exports = this.exports.map((item) => (item.id === exportId ? updated : item));
    } catch {
      // Left in editing state deliberately — an honest failure to save should not look like
      // a silent success, and the user's typed text stays right there to retry.
      return;
    }

    this.renamingId = null;
    this.render();
  }

  private async commitDelete(diagramId: string, exportId: string): Promise<void> {
    try {
      await this.deps.deleteExport(diagramId, exportId);
    } catch {
      this.confirmingDeleteId = null;
      this.render();
      return;
    }

    this.exports = this.exports.filter((item) => item.id !== exportId);
    this.confirmingDeleteId = null;
    this.render();
  }

  /** A larger view of one export's own SVG — the same `<img>` src as its list-row
   *  thumbnail, just bigger, plus the export's name and a download link. */
  private openViewer(diagramId: string, item: ExportDto): void {
    this.viewerOverlay?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'export-viewer-overlay';
    overlay.setAttribute('data-export-viewer', '');

    const panel = document.createElement('div');
    panel.className = 'export-viewer-panel';

    const header = document.createElement('div');
    header.className = 'export-viewer-header';

    const title = document.createElement('strong');
    title.textContent = describeName(item);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'dashboard-room-link';
    close.textContent = 'Close';
    close.addEventListener('click', () => this.closeViewer());

    header.append(title, close);

    const content =
      item.kind === EXPORT_KIND_MERMAID
        ? this.buildMermaidViewerContent(diagramId, item)
        : this.buildImageViewerContent(diagramId, item);

    const download = document.createElement('a');
    download.href = exportDownloadUrl(diagramId, item.id);
    download.download = `${describeName(item)}.${item.kind === EXPORT_KIND_MERMAID ? 'mmd' : 'svg'}`;
    download.textContent = 'Download';
    download.className = 'dashboard-room-link';

    panel.append(header, content, download);
    overlay.appendChild(panel);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        this.closeViewer();
      }
    });

    this.root.appendChild(overlay);
    this.viewerOverlay = overlay;
  }

  private buildImageViewerContent(diagramId: string, item: ExportDto): HTMLElement {
    const image = document.createElement('img');
    image.src = exportDownloadUrl(diagramId, item.id);
    image.alt = describeName(item);
    return image;
  }

  /**
   * Mermaid source, selectable, in the larger overlay view — reuses {@link mermaidText}'s
   * cache when the list's own preview cell already fetched it, and fetches independently
   * otherwise (opened via the "View" action rather than a click on the preview itself). The
   * overlay is its own DOM subtree, untouched by {@link render}'s table rebuild, so this
   * updates its own `<pre>` directly once the fetch settles rather than relying on a later
   * re-render to pick up the result.
   */
  private buildMermaidViewerContent(diagramId: string, item: ExportDto): HTMLElement {
    const pre = document.createElement('pre');
    pre.className = 'export-mermaid-viewer';

    const cached = this.mermaidText.get(item.id);
    if (cached !== undefined) {
      pre.textContent = cached;
      return pre;
    }

    pre.textContent = 'Loading…';

    void this.deps.getExportText(diagramId, item.id).then(
      (text) => {
        this.mermaidText.set(item.id, text);
        pre.textContent = text;
      },
      (error: unknown) => {
        pre.textContent = error instanceof Error ? error.message : 'Loading the mermaid source failed.';
      },
    );

    return pre;
  }

  private closeViewer(): void {
    this.viewerOverlay?.remove();
    this.viewerOverlay = null;
  }

  private query<T extends HTMLElement = HTMLElement>(selector: string): T {
    const found = this.root.querySelector<T>(selector);

    if (!found) {
      throw new Error(`PortalExports markup is missing ${selector}.`);
    }

    return found;
  }
}

/**
 * A display name for an export — its own `name` when it has one, or a description built
 * from `kind` and `createdAt` when it does not. Every export created since the `Name`
 * column shipped gets a sensible default at write time (`RoomSyncHub.ExportWhiteboard`),
 * but the column is purely additive (CLAUDE.md), so an export written before it existed has
 * `name: null` and always will — there is no backfill. This must render sensibly rather
 * than blank either way.
 */
function describeName(item: ExportDto): string {
  if (item.name && item.name.trim().length > 0) {
    return item.name;
  }

  return `${describeKind(item.kind)} export — ${formatCreatedAt(item.createdAt)}`;
}

/** `ExportKind` serialises as a number on the wire (no `JsonStringEnumConverter`
 *  configured — matches `NodeKind`'s own default) — `0` is the only value today. */
function describeKind(kind: number): string {
  if (kind === 0) {
    return 'Whiteboard';
  }

  if (kind === EXPORT_KIND_MERMAID) {
    return 'Mermaid';
  }

  return `Kind ${kind}`;
}

function formatCreatedAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  return `${(bytes / 1024).toFixed(1)} KB`;
}
